/**
 * TimesheetPage — Feuilles de temps RH
 * Onglet 1 : liste des feuilles, saisie/validation, export CSV
 * Onglet 2 : planning 4 semaines (lecture seule, admin peut modifier)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Clock, Calendar, Users, Download, Plus, ChevronLeft,
  Check, X, Trash2, FileText, Edit, Copy, ClipboardCheck,
  Send, Loader, Info,
} from 'lucide-react';
import {
  getTimeSheets, getTimeSheetsMultiStructures, getTimeSheet, createTimeSheet, deleteTimeSheet,
  updateTimeSheetStatus, updateTimeSheetEntry, updateTimeSheetVariable,
  exportTimesheetCsv, getSchedules, upsertSchedule, deleteSchedule, importSchedules,
  getEmployees, getTimesheetMotifs, getSchedulePlans,
} from '../api';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import PdfPreviewModal from '../components/ui/PdfPreviewModal';
import GlobalPlanningTab from '../components/hr/GlobalPlanningTab';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolvePlanForDate(plans, date) {
  const d = new Date(date);
  const override = plans
    .filter(p => !p.isReference && p.dateDebut && p.dateFin && new Date(p.dateDebut) <= d && new Date(p.dateFin) >= d)
    .sort((a, b) => new Date(b.dateDebut) - new Date(a.dateDebut))[0];
  return override ?? plans.find(p => p.isReference) ?? null;
}

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
};

const fmtDateShort = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const formatPeriod = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) return '—';
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  const from = new Date(dateFrom).toLocaleDateString('fr-FR', opts);
  const to = new Date(dateTo).toLocaleDateString('fr-FR', opts);
  return `${from} → ${to}`;
};

const formatHours = (minutes) => {
  if (minutes == null) return '';
  const h = Math.floor(Math.abs(minutes) / 60);
  const m = Math.abs(minutes) % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
};

const parseHoursInput = (str) => {
  if (!str || str.trim() === '') return null;
  const cleaned = str.trim().replace(',', '.');
  const matchHM = cleaned.match(/^(\d+)h(\d*)$/i);
  if (matchHM) {
    const h = parseInt(matchHM[1], 10);
    const m = matchHM[2] ? parseInt(matchHM[2], 10) : 0;
    return h * 60 + m;
  }
  const matchDecimal = parseFloat(cleaned);
  if (!isNaN(matchDecimal)) return Math.round(matchDecimal * 60);
  return null;
};

// ─── Constantes statuts ───────────────────────────────────────────────────────

const SHEET_STATUS_CONFIG = {
  draft:              { label: 'Brouillon',        color: 'bg-gray-100 text-gray-600' },
  submitted:          { label: 'Soumise',           color: 'bg-blue-100 text-blue-700' },
  validated_admin:    { label: 'Validée',           color: 'bg-green-100 text-green-700' },
  sent:               { label: 'Envoyée',           color: 'bg-indigo-100 text-indigo-700' },
};

// ─── Types de cellule traçabilité ─────────────────────────────────────────────

const ENTRY_TYPES = [
  { value: 'worked',    label: 'Travaillé',     color: 'bg-green-50 text-green-700', hasHours: true },
  { value: 'cp',        label: 'CP',            color: 'bg-blue-50 text-blue-700',   hasHours: false },
  { value: 'sick',      label: 'Maladie',       color: 'bg-orange-50 text-orange-700', hasHours: false },
  { value: 'accident',  label: 'AT',            color: 'bg-red-50 text-red-700',    hasHours: false },
  { value: 'maternity', label: 'Maternité',     color: 'bg-pink-50 text-pink-700',  hasHours: false },
  { value: 'unpaid',    label: 'Sans solde',    color: 'bg-red-100 text-red-800',   hasHours: false },
  { value: 'rcr',       label: 'RCR',           color: 'bg-violet-50 text-violet-700', hasHours: false },
];

const getEntryType = (value) => ENTRY_TYPES.find(t => t.value === value) || ENTRY_TYPES[0];

// ─── Couleurs employés ────────────────────────────────────────────────────────

const EMPLOYEE_COLORS = [
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-orange-100 text-orange-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-lime-100 text-lime-700',
];

// Couleur par index dans la liste triée des employés (pas par hash — garantit unicité)
const getEmployeeColorByIndex = (index) => EMPLOYEE_COLORS[index % EMPLOYEE_COLORS.length];

// ─── Jours de la semaine (planning) ──────────────────────────────────────────

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];

// ─── VARIABLE FIELDS ─────────────────────────────────────────────────────────

const VARIABLE_FIELDS = [
  { key: 'hc10',    label: 'HC 10%' },
  { key: 'hc25',    label: 'HC 25%' },
  { key: 'hs25',    label: 'HS 25%' },
  { key: 'hs50',    label: 'HS 50%' },
  { key: 'hn',      label: 'HN' },
  { key: 'rcrAcquis', label: 'RCR acquis' },
  { key: 'extra1',  label: 'Extra 1' },
  { key: 'extra2',  label: 'Extra 2' },
];

// ─── Composant Badge Statut ───────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cfg = SHEET_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ─── Composant Popover saisie cellule ─────────────────────────────────────────

const CellPopover = ({ entry, onSave, onClose }) => {
  const [type, setType] = useState(entry?.entryType || 'worked');
  const [hoursStr, setHoursStr] = useState(
    entry?.minutes != null ? formatHours(entry.minutes) : ''
  );
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const currentType = getEntryType(type);

  const handleSave = () => {
    const minutes = currentType.hasHours ? parseHoursInput(hoursStr) : null;
    onSave({ entryType: type, minutes });
  };

  return (
    <div
      ref={ref}
      className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-52"
      style={{ top: '100%', left: 0 }}
    >
      <div className="mb-2">
        <label className="block text-xs text-gray-500 mb-1">Type</label>
        <select
          className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
          value={type}
          onChange={e => setType(e.target.value)}
          autoFocus
        >
          {ENTRY_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
      {currentType.hasHours && (
        <div className="mb-2">
          <label className="block text-xs text-gray-500 mb-1">Heures (ex: 7h00)</label>
          <input
            type="text"
            className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            value={hoursStr}
            onChange={e => setHoursStr(e.target.value)}
            placeholder="7h00"
          />
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button
          onClick={onClose}
          className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700"
        >
          Annuler
        </button>
        <button
          onClick={handleSave}
          className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          OK
        </button>
      </div>
    </div>
  );
};

// ─── Cellule réelle éditable ──────────────────────────────────────────────────

const EditableCell = ({ entry, onUpdate, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const type = getEntryType(entry?.entryType || 'worked');
  const label = entry?.entryType
    ? (type.hasHours && entry.minutes != null
        ? formatHours(entry.minutes)
        : type.label)
    : '—';

  const handleSave = useCallback(async (data) => {
    setOpen(false);
    await onUpdate(data);
  }, [onUpdate]);

  if (disabled) {
    return (
      <div className={`text-center text-xs px-1 py-1 rounded ${entry?.entryType ? type.color : 'text-gray-300'}`}>
        {label}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full text-center text-xs px-1 py-1 rounded border transition-colors
          ${entry?.entryType ? type.color + ' border-transparent' : 'text-gray-300 border-gray-100 hover:border-indigo-300 hover:bg-indigo-50'}`}
      >
        {label || '+'}
      </button>
      {open && (
        <CellPopover
          entry={entry}
          onSave={handleSave}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const TimesheetPage = ({ user, showAllStructures = false, onNavigate }) => {
  const handleGoToEmployee = (empId) => {
    if (!empId || !onNavigate) return;
    sessionStorage.setItem('hr_open_employee', empId);
    sessionStorage.setItem('hr_came_from', 'timesheets');
    onNavigate('hr');
  };
  const { currentStructure, structures } = useStructure();
  const toast = useToast();

  const isAdmin = user?.role === 'admin';
  const isMulti = showAllStructures && structures.length >= 2;

  // Onglets
  const [activeTab, setActiveTab] = useState('sheets');

  // ── État onglet Feuilles ─────────────────────────────────────────────────────
  const [sheets, setSheets] = useState([]);
  const [loadingSheets, setLoadingSheets] = useState(true);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Modal création
  const [semaineAllPlans, setSemaineAllPlans] = useState([]);
  const [semaineActivePlanId, setSemaineActivePlanId] = useState(null);
  const [semaineSelectedPlanId, setSemaineSelectedPlanId] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    structureId: '', label: '', dateFrom: '', dateTo: '', startWeek: 1,
  });
  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewPlanLoading, setPreviewPlanLoading] = useState(false);

  // Statut update
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Label editable
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelForm, setLabelForm] = useState('');

  // Variables inline edit
  const [editingVars, setEditingVars] = useState({});

  // Employé sélectionné dans la vue détail
  const [selectedEmpId, setSelectedEmpId] = useState('');
  // Sous-onglet vue détail
  const [detailTab, setDetailTab] = useState('saisie'); // saisie|activite|heures|variables
  // Preview PDF (Cabinet RH, feuilles vierges, planning, etc.)
  const [pdfPreview, setPdfPreview] = useState(null); // { doc, fileName }

  // Modale de sélection employés pour l'export "Feuilles papier (PDF)"
  // Persistance localStorage par structureId (clé : timesheet-blank-hidden-emp-{structureId})
  const [showBlankPdfModal, setShowBlankPdfModal] = useState(false);
  const [blankPdfSelectedIds, setBlankPdfSelectedIds] = useState([]);
  const [blankPdfGenerating, setBlankPdfGenerating] = useState(false);

  // ── État onglet Planning ─────────────────────────────────────────────────────
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null); // { empId, week, day }
  const [slotForm, setSlotForm] = useState({ startTime: '', endTime: '', pauseMinutes: 60 });
  const [savingSlot, setSavingSlot] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [motifs, setMotifs] = useState([]);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [addEmpId, setAddEmpId] = useState('');
  const [showCopyEmployee, setShowCopyEmployee] = useState(null); // source empId
  const [copyTargetEmpId, setCopyTargetEmpId] = useState('');
  const [planningFilterEmpId, setPlanningFilterEmpId] = useState(''); // '' = tous
  const [planningView, setPlanningView] = useState('plans'); // 'plans' | 'semaine'

  // ─── Chargement feuilles ─────────────────────────────────────────────────────

  const loadSheets = useCallback(async () => {
    if (!currentStructure?.id) return;
    setLoadingSheets(true);
    try {
      if (isMulti) {
        const ids = structures.map(s => s.id);
        const res = await getTimeSheetsMultiStructures(ids);
        setSheets(res.data || []);
      } else {
        const res = await getTimeSheets({ structureId: currentStructure.id });
        setSheets(res.data || []);
      }
    } catch {
      toast.error('Erreur lors du chargement des feuilles');
    } finally {
      setLoadingSheets(false);
    }
  }, [currentStructure?.id, isMulti, structures, toast]);

  useEffect(() => { loadSheets(); }, [loadSheets]);
  useEffect(() => {
    getTimesheetMotifs().then(res => setMotifs(res.data || [])).catch(() => {});
  }, []);

  // ─── Chargement planning ──────────────────────────────────────────────────────

  const loadSchedules = useCallback(async () => {
    if (!currentStructure?.id) return;
    setLoadingSchedules(true);
    try {
      const res = await getSchedules({ structureId: currentStructure.id });
      setSchedules(res.data || []);
    } catch {
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoadingSchedules(false);
    }
  }, [currentStructure?.id, toast]);

  // Chargement des employés dès le montage (pas conditionné à l'onglet actif)
  useEffect(() => {
    if (!currentStructure?.id) return;
    getEmployees().then(res => {
      setAllEmployees((res.data || []).filter(e => e.status === 'active'));
    }).catch(() => {});
  }, [currentStructure?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'semaine') {
      loadSchedules();
      if (currentStructure?.id) {
        // Charger les plans pour le sélecteur de vue
        getSchedulePlans(currentStructure.id).then(res => {
          const { plans, activePlanId } = res.data;
          setSemaineAllPlans(plans || []);
          setSemaineActivePlanId(activePlanId ?? null);
          setSemaineSelectedPlanId(prev =>
            prev && (plans || []).find(p => p.id === prev)
              ? prev
              : (activePlanId ?? (plans || [])[0]?.id ?? null)
          );
        }).catch(() => {});
      }
    }
  }, [activeTab, loadSchedules, currentStructure?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sélection d'une feuille ──────────────────────────────────────────────────

  const openSheet = useCallback(async (sheet) => {
    setLoadingDetail(true);
    setSelectedSheet(null);
    try {
      const res = await getTimeSheet(sheet.id);
      setSelectedSheet(res.data);
      setEditingVars({});
    } catch {
      toast.error('Erreur lors du chargement de la feuille');
    } finally {
      setLoadingDetail(false);
    }
  }, [toast]);

  // ─── Aperçu plan appliqué (modale création) ──────────────────────────────────

  useEffect(() => {
    if (!showCreateModal || !createForm.dateFrom) {
      setPreviewPlan(null);
      return;
    }
    const structId = isMulti ? createForm.structureId : currentStructure?.id;
    if (!structId) { setPreviewPlan(null); return; }
    let cancelled = false;
    setPreviewPlanLoading(true);
    getSchedulePlans(structId)
      .then(res => {
        if (cancelled) return;
        setPreviewPlan(resolvePlanForDate(res.data.plans, createForm.dateFrom));
      })
      .catch(() => { if (!cancelled) setPreviewPlan(null); })
      .finally(() => { if (!cancelled) setPreviewPlanLoading(false); });
    return () => { cancelled = true; };
  }, [showCreateModal, createForm.dateFrom, createForm.structureId, currentStructure?.id, isMulti]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Création feuille ─────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!createForm.label || !createForm.dateFrom || !createForm.dateTo || (isMulti && !createForm.structureId)) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setCreating(true);
    try {
      await createTimeSheet({
        structureId: isMulti ? createForm.structureId : currentStructure.id,
        label: createForm.label,
        dateFrom: createForm.dateFrom,
        dateTo: createForm.dateTo,
        cycleStartWeek: parseInt(createForm.startWeek, 10),
      });
      toast.success('Feuille de temps creee');
      setShowCreateModal(false);
      setCreateForm({ structureId: '', label: '', dateFrom: '', dateTo: '', startWeek: 1 });
      await loadSheets();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur lors de la creation');
    } finally {
      setCreating(false);
    }
  };

  // ─── Suppression feuille ──────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette feuille de temps ?')) return;
    setDeletingId(id);
    try {
      await deleteTimeSheet(id);
      toast.success('Feuille de temps supprimee');
      if (selectedSheet?.id === id) setSelectedSheet(null);
      await loadSheets();
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  // ─── Mise à jour statut ───────────────────────────────────────────────────────

  const handleStatusChange = async (newStatus) => {
    if (!selectedSheet) return;
    setUpdatingStatus(true);
    try {
      await updateTimeSheetStatus(selectedSheet.id, newStatus);
      const updated = await getTimeSheet(selectedSheet.id);
      setSelectedSheet(updated.data);
      await loadSheets();
      toast.success('Feuille de temps modifiee');
    } catch {
      toast.error('Erreur lors de la mise a jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ─── Mise à jour entrée ───────────────────────────────────────────────────────

  const handleUpdateEntry = useCallback(async (entryId, data) => {
    try {
      await updateTimeSheetEntry(entryId, data);
      const updated = await getTimeSheet(selectedSheet.id);
      setSelectedSheet(updated.data);
    } catch {
      toast.error('Erreur lors de la mise a jour');
    }
  }, [selectedSheet?.id, toast]);

  // ─── Mise à jour variables ────────────────────────────────────────────────────

  const handleSaveVariables = async (employeeId) => {
    const vars = editingVars[employeeId] || {};
    try {
      await updateTimeSheetVariable(selectedSheet.id, employeeId, vars);
      const updated = await getTimeSheet(selectedSheet.id);
      setSelectedSheet(updated.data);
      toast.success('Saisie enregistree');
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // ─── Export CSV ───────────────────────────────────────────────────────────────

  const handleExportCsv = async (sheetId, label) => {
    try {
      const res = await exportTimesheetCsv(sheetId);
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feuille-temps-${label || sheetId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors de l\'export');
    }
  };

  // ─── Helpers planning ─────────────────────────────────────────────────────────

  // Schedules filtrés par plan sélectionné dans "Vue par semaine"
  const semaineViewPlan = semaineAllPlans.find(p => p.id === semaineSelectedPlanId) ?? null;
  const displaySchedules = semaineSelectedPlanId && semaineAllPlans.length > 0
    ? schedules.filter(s => s.planId === semaineSelectedPlanId)
    : schedules;

  // Regrouper les schedules par employé + semaine + jour
  const buildPlanningGrid = () => {
    const grid = {};
    displaySchedules.forEach(s => {
      const empId = s.employeeId;
      const week = s.weekNumber || 1;
      const day = s.dayOfWeek;
      if (!grid[empId]) grid[empId] = { employee: s.employee, weeks: {} };
      if (!grid[empId].weeks[week]) grid[empId].weeks[week] = {};
      grid[empId].weeks[week][day] = s;
    });
    return grid;
  };

  const planningGrid = buildPlanningGrid();
  const employeeIds = Object.keys(planningGrid);
  const planningWeeks = semaineViewPlan?.nbSemaines
    ?? Math.max(1, ...displaySchedules.map(s => s.weekNumber || 1));

  // ─── Bouton statut contextuel ─────────────────────────────────────────────────

  const renderStatusAction = () => {
    if (!selectedSheet) return null;
    const s = selectedSheet.status;
    const buttons = [];

    // Brouillon : tout le monde peut soumettre, admin peut aussi valider directement
    if (s === 'draft') {
      buttons.push(
        <button key="submit" disabled={updatingStatus} onClick={() => handleStatusChange('submitted')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50">
          <Send size={14} /> Soumettre à validation
        </button>
      );
      if (isAdmin) {
        buttons.push(
          <button key="validate-direct" disabled={updatingStatus} onClick={() => handleStatusChange('validated_admin')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50">
            <Check size={14} /> Valider
          </button>
        );
      }
    }

    // Soumise : admin valide ou renvoie en correction
    if (s === 'submitted' && isAdmin) {
      buttons.push(
        <button key="validate" disabled={updatingStatus} onClick={() => handleStatusChange('validated_admin')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50">
          <Check size={14} /> Valider
        </button>,
        <button key="reject" disabled={updatingStatus} onClick={() => handleStatusChange('draft')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm disabled:opacity-50">
          <ChevronLeft size={14} /> Renvoyer en correction
        </button>
      );
    }

    // Validée : admin envoie ou renvoie
    if (s === 'validated_admin' && isAdmin) {
      buttons.push(
        <button key="send" disabled={updatingStatus} onClick={() => handleStatusChange('sent')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50">
          <Send size={14} /> Marquer comme envoyée
        </button>,
        <button key="reject-validated" disabled={updatingStatus} onClick={() => handleStatusChange('draft')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm disabled:opacity-50">
          <ChevronLeft size={14} /> Renvoyer en correction
        </button>
      );
    }

    return buttons;
  };

  // ─── Helpers vue détail ───────────────────────────────────────────────────────

  const EVENT_TYPES = [
    { value: '', label: '—' },
    { value: 'hs_paid', label: 'HS payée' },
    { value: 'hs_recup', label: 'HS → récup' },
    { value: 'recup_taken', label: 'RCR prise (compteur)' },
    { value: 'recup_week', label: 'Récupération (compensée semaine)' },
    { value: 'ferie', label: 'Jour férié' },
    { value: 'cp_partial', label: 'CP (congé payé)' },
    { value: 'sick', label: 'Maladie' },
    { value: 'accident', label: 'Accident de travail' },
    { value: 'maternity', label: 'Maternité' },
    { value: 'unpaid_partial', label: 'Sans solde' },
  ];

  const DAYS_SHORT = ['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const formatTime = (t) => t || '—';
  const formatDecimalHours = (hours) => {
    if (hours == null) return '—';
    const h = Math.floor(Math.abs(hours));
    const m = Math.round((Math.abs(hours) - h) * 60);
    const sign = hours < 0 ? '-' : '';
    return m === 0 ? `${sign}${h}h` : `${sign}${h}h${String(m).padStart(2, '0')}`;
  };

  const groupEntriesByEmployee = (entries) => {
    const grouped = {};
    for (const e of entries) {
      const empId = e.employeeId;
      if (!grouped[empId]) grouped[empId] = { employee: e.employee, entries: [] };
      grouped[empId].entries.push(e);
    }
    return Object.values(grouped);
  };

  const getWeekNumber = (date, dateFrom) => {
    const d = new Date(date);
    const start = new Date(dateFrom);
    d.setHours(0,0,0,0);
    start.setHours(0,0,0,0);
    return Math.floor((d - start) / (7 * 24 * 60 * 60 * 1000)) + 1;
  };

  // ─── Saisie inline d'une cellule horaire ────────────────────────────────────────

  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({});
  const [savingEntry, setSavingEntry] = useState(false);

  const startEditEntry = (entry) => {
    setEditingEntry(entry.id);
    setEntryForm({
      actualStart: entry.actualStart || '',
      actualEnd: entry.actualEnd || '',
      pauseDuration: entry.pauseDuration ?? 60,
      type: entry.type || 'worked',
      eventType: entry.eventType || '',
      motifId: entry.motifId || '',
      notes: entry.notes || '',
    });
  };

  const saveEntryEdit = async (entryId) => {
    setSavingEntry(true);
    try {
      await updateTimeSheetEntry(entryId, entryForm);
      const updated = await getTimeSheet(selectedSheet.id);
      setSelectedSheet(updated.data);
      setEditingEntry(null);
      toast.success('Enregistré');
    } catch {
      toast.error('Erreur sauvegarde');
    }
    setSavingEntry(false);
  };

  const cancelEditEntry = () => {
    setEditingEntry(null);
    setEntryForm({});
  };

  // ─── Rendu vue détail feuille (format feuille papier) ───────────────────────────

  const renderSheetDetail = () => {
    if (!selectedSheet) return null;
    const { label, dateFrom, dateTo, status, entries, variables } = selectedSheet;

    // Envoyée = lecture seule pour tous. Soumise/validée = lecture seule sauf admin
    const isReadOnly = status === 'sent' || (!isAdmin && (status === 'submitted' || status === 'validated_admin'));
    const employeeGroups = entries ? groupEntriesByEmployee(entries) : [];

    // Si pas d'employé sélectionné, prendre le premier
    const effectiveEmpId = selectedEmpId || employeeGroups[0]?.employee?.id || '';
    const selectedGroup = employeeGroups.find(g => g.employee?.id === effectiveEmpId) || employeeGroups[0];

    if (!selectedGroup) {
      return (
        <div>
          <button onClick={() => setSelectedSheet(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-600 mb-4">
            <ChevronLeft size={16} /> Retour
          </button>
          <div className="text-center py-12 text-gray-400">
            <FileText size={40} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune donnée dans cette feuille.</p>
          </div>
        </div>
      );
    }

    const empEntries = selectedGroup.entries.sort((a, b) => new Date(a.date) - new Date(b.date));
    const empName = `${selectedGroup.employee.firstName} ${selectedGroup.employee.lastName}`;
    const empWorkHours = selectedGroup.employee.workHours || 35;

    // Grouper par semaine
    const weeks = {};
    empEntries.forEach(e => {
      const wk = getWeekNumber(e.date, dateFrom);
      if (!weeks[wk]) weeks[wk] = [];
      weeks[wk].push(e);
    });

    // Calcul totaux
    let totalScheduled = 0;
    let totalActual = 0;
    let totalOvertime = 0;

    return (
      <div>
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button onClick={() => setSelectedSheet(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-600">
            <ChevronLeft size={16} /> Retour
          </button>
          <div className="flex-1 flex items-center gap-3 flex-wrap">
            {editingLabel ? (
              <div className="flex items-center gap-2">
                <input type="text" value={labelForm} onChange={e => setLabelForm(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { handleStatusChange(null); } }}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm font-semibold" autoFocus />
                <button onClick={async () => {
                  try {
                    await updateTimeSheetStatus(selectedSheet.id, null, labelForm);
                    const updated = await getTimeSheet(selectedSheet.id);
                    setSelectedSheet(updated.data);
                    setEditingLabel(false);
                    await loadSheets();
                    toast.success('Label mis à jour');
                  } catch { toast.error('Erreur'); }
                }} className="p-1 bg-green-600 text-white rounded"><Check size={14} /></button>
                <button onClick={() => setEditingLabel(false)} className="p-1 bg-gray-200 text-gray-600 rounded"><X size={14} /></button>
              </div>
            ) : (
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-1">
                {label}
                {!isReadOnly && <button onClick={() => { setEditingLabel(true); setLabelForm(label); }} className="p-1 text-gray-400 hover:text-indigo-600 rounded"><Edit size={12} /></button>}
              </h2>
            )}
            <StatusBadge status={status} />
            <span className="text-sm text-gray-500">{formatPeriod(dateFrom, dateTo)}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {renderStatusAction()}
            <button onClick={() => handleExportCsv(selectedSheet.id, label)} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              <Download size={14} /> CSV
            </button>
            <button onClick={() => {
              // Ouvrir la modale de sélection employés
              const empIds = Array.from(new Set((selectedSheet.entries || []).map(e => e.employeeId))).filter(Boolean);
              // Filtre localStorage (employés masqués par défaut pour cette structure)
              const sid = selectedSheet.structureId || currentStructure?.id || '';
              const hiddenKey = `timesheet-blank-hidden-emp-${sid}`;
              let hidden = [];
              try {
                hidden = JSON.parse(localStorage.getItem(hiddenKey) || '[]');
              } catch { hidden = []; }
              const preSelected = empIds.filter(id => !hidden.includes(id));
              setBlankPdfSelectedIds(preSelected);
              setShowBlankPdfModal(true);
            }} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              <FileText size={14} /> Feuilles papier (PDF)
            </button>
            {isAdmin && (status === 'validated_admin' || status === 'sent') && (
              <button onClick={async () => {
                const { generateCabinetRHPdf } = await import('../utils/timesheetPdf');
                const { getStructureIdentity } = await import('../api');
                let structName = currentStructure?.name || '';
                try {
                  const res = await getStructureIdentity(selectedSheet.structureId || currentStructure?.id);
                  if (res.data?.fullName) structName = res.data.fullName;
                } catch {}
                const result = await generateCabinetRHPdf(selectedSheet, structName, { preview: true });
                if (result) setPdfPreview(result);
              }} className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm">
                <FileText size={14} /> Cabinet RH (PDF)
              </button>
            )}
          </div>
        </div>

        {/* Sélecteur employé */}
        <div className="flex items-center gap-3 mb-4 bg-indigo-50 rounded-xl px-4 py-3">
          <Users size={16} className="text-indigo-600" />
          <select
            value={effectiveEmpId}
            onChange={e => { setSelectedEmpId(e.target.value); setEditingEntry(null); }}
            className="bg-white border border-indigo-200 rounded-lg px-3 py-1.5 text-sm font-medium focus:ring-2 focus:ring-indigo-400"
          >
            {employeeGroups.map(g => (
              <option key={g.employee.id} value={g.employee.id}>
                {g.employee.firstName} {g.employee.lastName}
              </option>
            ))}
          </select>
          <span className="text-sm text-indigo-600">Contrat : {empWorkHours}h/semaine</span>
          {isAdmin && !isReadOnly && (
            <button onClick={async () => {
              if (!window.confirm(`Attention : toutes les donnees saisies pour ${empName} seront reinitialisees depuis le planning actuel.\n\nLes heures, evenements, motifs et observations seront effaces.\n\nContinuer ?`)) return;
              try {
                const { regenerateTimesheetSchedule } = await import('../api');
                const res = await regenerateTimesheetSchedule(selectedSheet.id, effectiveEmpId);
                toast.success(`${res.data.updated} entrées mises à jour`);
                const updated = await getTimeSheet(selectedSheet.id);
                setSelectedSheet(updated.data);
              } catch { toast.error('Erreur actualisation'); }
            }} className="flex items-center gap-1 px-2 py-1 border border-amber-300 text-amber-700 rounded-lg text-xs hover:bg-amber-50"
              title="Actualise les horaires planifiés de cet employé depuis le planning actuel">
              <Calendar size={12} /> Actualiser planning
            </button>
          )}
        </div>

        {/* Sous-onglets */}
        <div className="flex gap-1 mb-4 border-b border-gray-200">
          {[
            { key: 'saisie', label: 'Saisie', icon: Edit },
            { key: 'activite', label: 'Activité', icon: Calendar },
            ...(isAdmin ? [
              { key: 'heures', label: 'Heures', icon: Clock },
              { key: 'variables', label: 'Éléments variables', icon: FileText },
            ] : []),
          ].map(tab => (
            <button key={tab.key} onClick={() => setDetailTab(tab.key)}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px flex items-center gap-1
                ${detailTab === tab.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <tab.icon size={13} /> {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ SOUS-ONGLET SAISIE ═══ */}
        {detailTab === 'saisie' && (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase w-24">Date</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase w-12">Jour</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase w-20">Arrivée</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase w-20">Sortie</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase w-16">Pause</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase w-16">Total</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase w-16">Planifié</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase w-16">Écart</th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase w-32">Événement</th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase w-32">Motif</th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase min-w-[150px]">Observation</th>
                {!isReadOnly && <th className="px-2 py-2.5 w-20"></th>}
              </tr>
            </thead>
            <tbody>
              {Object.entries(weeks).map(([weekNum, weekEntries]) => {
                let weekScheduled = 0;
                let weekActual = 0;

                const rows = weekEntries.map((entry, idx) => {
                  const d = new Date(entry.date);
                  const dayOfWeek = d.getDay(); // 0=dim
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const actual = entry.actualHours ? Number(entry.actualHours) : null;
                  const scheduled = entry.scheduledHours ? Number(entry.scheduledHours) : null;
                  // recup_taken n'est PAS une absence : l'employee a travaille actual heures
                  // et le manquant vs scheduled est compense par le compteur RCR.
                  // Les heures reelles doivent compter dans le cumul semaine.
                  // ferie est exclu du cumul (jour non travaille), comme les absences
                  const ABSENCE_EVENTS = ['cp_partial', 'sick', 'accident', 'maternity', 'unpaid_partial', 'ferie'];
                  const isAbsenceEvent = entry.eventType && ABSENCE_EVENTS.includes(entry.eventType);

                  // Écart journalier : si absence totale (pas d'heures), écart = -planifié
                  let dailyDiff = 0;
                  if (isAbsenceEvent && (actual == null || actual === 0) && scheduled) {
                    dailyDiff = -scheduled; // journée complète absente
                  } else if (isAbsenceEvent && actual != null && actual > 0 && scheduled) {
                    dailyDiff = actual - scheduled; // partie de journée
                  } else if (actual != null && scheduled != null) {
                    dailyDiff = actual - scheduled;
                  }

                  const isEditing = editingEntry === entry.id;

                  if (actual && entry.type === 'worked' && !isAbsenceEvent) { weekActual += actual; totalActual += actual; }
                  // scheduled exclu si absence reelle (pas de travail effectif) - sinon
                  // l'ecart hebdo serait fausse (ex: maladie 8h baisserait le cumul de 8h).
                  if (scheduled && !isAbsenceEvent) { weekScheduled += scheduled; totalScheduled += scheduled; }

                  const isNonWorked = ['cp', 'sick', 'accident', 'maternity', 'patho', 'unpaid', 'absence', 'off'].includes(entry.type);

                  return (
                    <tr key={entry.id} className={`border-b border-gray-50 transition ${isWeekend ? 'bg-gray-50/50' : 'hover:bg-indigo-50/20'} ${isEditing ? 'bg-indigo-50' : ''}`}>
                      <td className="px-3 py-2 text-gray-700 font-medium">{d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</td>
                      <td className="px-2 py-2 text-center text-gray-500 text-xs">{DAYS_SHORT[dayOfWeek === 0 ? 7 : dayOfWeek]}</td>

                      {isEditing ? (
                        <>
                          <td className="px-1 py-1"><input type="time" value={entryForm.actualStart} onChange={e => setEntryForm(f => ({...f, actualStart: e.target.value}))} className="w-full border rounded px-1 py-0.5 text-xs text-center" /></td>
                          <td className="px-1 py-1"><input type="time" value={entryForm.actualEnd} onChange={e => setEntryForm(f => ({...f, actualEnd: e.target.value}))} className="w-full border rounded px-1 py-0.5 text-xs text-center" /></td>
                          <td className="px-1 py-1"><input type="number" min="0" max="120" step="15" value={entryForm.pauseDuration} onChange={e => setEntryForm(f => ({...f, pauseDuration: Number(e.target.value)}))} className="w-full border rounded px-1 py-0.5 text-xs text-center" /></td>
                          <td className="px-2 py-2 text-center text-xs text-gray-400">auto</td>
                          <td className="px-2 py-2 text-center text-xs text-gray-400">{formatDecimalHours(scheduled)}</td>
                          <td className="px-2 py-2 text-center text-xs text-gray-400">auto</td>
                          <td className="px-1 py-1">
                            <select value={entryForm.eventType} onChange={e => {
                              const v = e.target.value;
                              setEntryForm(f => ({
                                ...f,
                                eventType: v,
                                // Ferie : pas d'heures reelles (jour non travaille, paye forfait)
                                ...(v === 'ferie' ? { actualStart: '', actualEnd: '' } : {}),
                              }));
                            }} className="w-full border rounded px-1 py-0.5 text-xs">
                              {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1">
                            <select value={entryForm.motifId || ''} onChange={e => setEntryForm(f => ({...f, motifId: e.target.value || null}))} className="w-full border rounded px-1 py-0.5 text-xs">
                              <option value="">—</option>
                              {motifs.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                          </td>
                          <td className="px-1 py-1"><input type="text" value={entryForm.notes} onChange={e => setEntryForm(f => ({...f, notes: e.target.value}))} className="w-full border rounded px-1 py-0.5 text-xs" placeholder="Observation..." /></td>
                          <td className="px-1 py-1">
                            <div className="flex gap-1">
                              <button onClick={() => saveEntryEdit(entry.id)} disabled={savingEntry} className="p-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"><Check size={12} /></button>
                              <button onClick={cancelEditEntry} className="p-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300"><X size={12} /></button>
                            </div>
                          </td>
                        </>
                      ) : isNonWorked ? (
                        <>
                          <td colSpan={3} className="px-2 py-2 text-center">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getEntryType(entry.type).color}`}>
                              {getEntryType(entry.type).label}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-center text-xs text-gray-400">—</td>
                          <td className="px-2 py-2 text-center text-xs text-gray-400">{formatDecimalHours(scheduled)}</td>
                          <td className="px-2 py-2 text-center text-xs text-gray-400">—</td>
                          <td className="px-2 py-2 text-xs text-gray-500">{entry.eventType ? EVENT_TYPES.find(t => t.value === entry.eventType)?.label : ''}</td>
                          <td className="px-2 py-2 text-xs text-gray-500">{entry.motif?.name || ''}</td>
                          <td className="px-2 py-2 text-xs text-gray-500">{entry.notes || ''}</td>
                          {!isReadOnly && (
                            <td className="px-1 py-1">
                              <button onClick={() => startEditEntry(entry)} className="p-1 text-gray-400 hover:text-indigo-600 rounded"><Edit size={12} /></button>
                            </td>
                          )}
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-center text-xs font-medium text-gray-700">{formatTime(entry.actualStart)}</td>
                          <td className="px-2 py-2 text-center text-xs font-medium text-gray-700">{formatTime(entry.actualEnd)}</td>
                          <td className="px-2 py-2 text-center text-xs text-gray-500">{entry.pauseDuration ?? 60} min</td>
                          <td className="px-2 py-2 text-center text-xs font-semibold text-gray-800">{formatDecimalHours(actual)}</td>
                          <td className="px-2 py-2 text-center text-xs text-gray-400">{formatDecimalHours(scheduled)}</td>
                          <td className={`px-2 py-2 text-center text-xs font-semibold ${dailyDiff > 0.01 ? 'text-red-600' : dailyDiff < -0.01 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {Math.abs(dailyDiff) > 0.01 ? (dailyDiff > 0 ? '+' : '') + formatDecimalHours(dailyDiff) : '—'}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-600">
                            {entry.eventType ? (
                              <span className="inline-flex px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-xs">{EVENT_TYPES.find(t => t.value === entry.eventType)?.label || entry.eventType}</span>
                            ) : ''}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-500">{entry.motif?.name || ''}</td>
                          <td className="px-2 py-2 text-xs text-gray-500">{entry.notes || ''}</td>
                          {!isReadOnly && (
                            <td className="px-1 py-1">
                              <button onClick={() => startEditEntry(entry)} className="p-1 text-gray-400 hover:text-indigo-600 rounded"><Edit size={12} /></button>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  );
                });

                return (
                  <React.Fragment key={weekNum}>
                    {rows}
                    {/* Cumul semaine */}
                    {(() => {
                      // HS basé sur le planifié de la semaine (pas le contrat fixe)
                      const weekOT = weekScheduled > 0 ? weekActual - weekScheduled : 0;
                      totalOvertime += Math.max(weekOT, 0); // HS positives uniquement
                      return (
                        <tr className="bg-indigo-50/40 border-b-2 border-indigo-100">
                          <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-indigo-700">Cumul Semaine {weekNum}</td>
                          <td className="px-2 py-2 text-center text-xs font-bold text-indigo-700" colSpan={2}></td>
                          <td className="px-2 py-2 text-center text-xs font-bold text-indigo-800">{formatDecimalHours(weekActual)}</td>
                          <td className="px-2 py-2 text-center text-xs font-medium text-gray-500">{formatDecimalHours(weekScheduled)}</td>
                          <td className={`px-2 py-2 text-center text-xs font-bold ${weekOT > 0.01 ? 'text-red-600' : weekOT < -0.01 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {Math.abs(weekOT) > 0.01 ? (weekOT > 0 ? '+' : '') + formatDecimalHours(weekOT) : '—'}
                          </td>
                          <td colSpan={!isReadOnly ? 4 : 3}></td>
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
              {/* Total période + ventilation */}
              {(() => {
                // Calculer les totaux par type d'événement
                let totalHS = 0, totalRCR = 0, totalCP = 0, totalSick = 0, totalAT = 0, totalMAT = 0, totalCSS = 0;
                let totalFerie = 0, debtDelta = 0;
                const dailyBase = Number(empWorkHours) / 5;
                empEntries.forEach(e => {
                  const planned = e.scheduledHours ? Number(e.scheduledHours) : 0;
                  const actual = e.actualHours ? Number(e.actualHours) : 0;
                  if (e.eventType === 'cp_partial') totalCP += planned;
                  if (e.eventType === 'sick') totalSick += planned;
                  if (e.eventType === 'accident') totalAT += planned;
                  if (e.eventType === 'maternity') totalMAT += planned;
                  if (e.eventType === 'unpaid_partial') totalCSS += planned;
                  if (e.eventType === 'recup_taken') {
                    totalRCR += e.overtimeMinutes ? Math.abs(e.overtimeMinutes / 60) : planned;
                  }
                  if (e.eventType === 'ferie') {
                    totalFerie += planned;
                    debtDelta += (planned - dailyBase);
                  }
                  if (e.eventType === 'recup_week' && actual > planned) {
                    debtDelta -= (actual - planned);
                  }
                });
                totalHS = totalOvertime;

                return (
                  <>
                    <tr className="bg-gray-100 border-t-2 border-gray-300">
                      <td colSpan={3} className="px-3 py-3 text-sm font-bold text-gray-800">Total {empName}</td>
                      <td className="px-2 py-3 text-center text-sm font-bold text-gray-800" colSpan={2}></td>
                      <td className="px-2 py-3 text-center text-sm font-bold text-gray-800">{formatDecimalHours(totalActual)}</td>
                      <td className="px-2 py-3 text-center text-sm font-medium text-gray-600">{formatDecimalHours(totalScheduled)}</td>
                      <td colSpan={!isReadOnly ? 5 : 4}></td>
                    </tr>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <td colSpan={isReadOnly ? 11 : 12} className="px-3 py-2">
                        <div className="flex flex-wrap gap-4 text-xs">
                          {totalHS > 0.01 && <span className="font-semibold text-red-600">HS : {formatDecimalHours(totalHS)}</span>}
                          {totalRCR > 0.01 && <span className="font-semibold text-violet-600">RCR : {formatDecimalHours(totalRCR)}</span>}
                          {totalCP > 0.01 && <span className="font-semibold text-blue-600">CP : {formatDecimalHours(totalCP)}</span>}
                          {totalSick > 0.01 && <span className="font-semibold text-orange-600">M : {formatDecimalHours(totalSick)}</span>}
                          {totalAT > 0.01 && <span className="font-semibold text-red-600">AT : {formatDecimalHours(totalAT)}</span>}
                          {totalMAT > 0.01 && <span className="font-semibold text-pink-600">MAT : {formatDecimalHours(totalMAT)}</span>}
                          {totalCSS > 0.01 && <span className="font-semibold text-red-500">CSS : {formatDecimalHours(totalCSS)}</span>}
                          {totalFerie > 0.01 && <span className="font-semibold text-amber-700">Férié : {formatDecimalHours(totalFerie)}</span>}
                          {Math.abs(debtDelta) > 0.01 && (
                            <span className={`font-semibold ${debtDelta > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                              Dette : {debtDelta > 0 ? '+' : ''}{formatDecimalHours(debtDelta)}
                            </span>
                          )}
                          {totalHS <= 0.01 && totalRCR <= 0.01 && totalCP <= 0.01 && totalSick <= 0.01 && totalAT <= 0.01 && totalMAT <= 0.01 && totalCSS <= 0.01 && totalFerie <= 0.01 && Math.abs(debtDelta) <= 0.01 && (
                            <span className="text-gray-400">Aucun écart</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
        )}

        {/* ═══ SOUS-ONGLET ACTIVITÉ (format Silae) ═══ */}
        {detailTab === 'activite' && (() => {
          const TYPE_CODES = { worked: '', cp: 'CP', sick: 'M', accident: 'AT', maternity: 'MAT', patho: 'PAT', rcr: 'RCR', unpaid: 'CSS', absence: 'ABS', off: 'R', event: 'EVT' };
          const EVENT_CODES = { recup_taken: 'RCR', cp_partial: 'CP', sick: 'M', accident: 'AT', maternity: 'MAT', unpaid_partial: 'CSS', ferie: 'F', recup_week: 'R' };
          const TYPE_COLORS = { cp: 'bg-blue-100 text-blue-700', sick: 'bg-orange-100 text-orange-700', accident: 'bg-red-100 text-red-700', maternity: 'bg-pink-100 text-pink-700', rcr: 'bg-violet-100 text-violet-700', unpaid: 'bg-red-50 text-red-600' };
          const EVENT_COLORS = { recup_taken: 'bg-violet-50 text-violet-600', cp_partial: 'bg-blue-50 text-blue-600', sick: 'bg-orange-50 text-orange-600', accident: 'bg-red-50 text-red-600', maternity: 'bg-pink-50 text-pink-600', unpaid_partial: 'bg-red-50 text-red-500', ferie: 'bg-amber-50 text-amber-700', recup_week: 'bg-teal-50 text-teal-700' };

          // Toutes les dates de la période
          const allDates = [];
          const cur = new Date(dateFrom);
          const end = new Date(dateTo);
          while (cur <= end) { allDates.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
          // Filtrer jours ouvrés
          const workDates = allDates.filter(d => { const dow = d.getDay(); return dow >= 1 && dow <= 5; });

          const copyToClipboard = (text) => {
            navigator.clipboard.writeText(text);
            toast.success('Copié');
          };

          return (
            <div>
              <p className="text-xs text-gray-500 mb-3">Vue Activité — à reporter dans MySilae onglet "Activité"</p>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="min-w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-500 min-w-[140px]">Employé</th>
                      {workDates.map((d, i) => (
                        <th key={i} className="px-1 py-2 text-center text-xs font-medium text-gray-500 min-w-[36px]">
                          <div>{d.getDate()}</div>
                          <div className="text-gray-400 text-[9px]">{DAYS_SHORT[d.getDay()]}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {employeeGroups.map((group, gi) => {
                      const emp = group.employee;
                      return (
                        <tr key={emp.id} className="border-b border-gray-50 hover:bg-gray-50/40">
                          <td className="sticky left-0 bg-white px-3 py-2 font-medium text-gray-700">
                            <button onClick={() => handleGoToEmployee(emp.id)} title="Voir la fiche RH" className={`text-left ${onNavigate ? 'hover:text-primary hover:underline' : ''}`}>
                              {emp.firstName} {emp.lastName}
                            </button>
                          </td>
                          {workDates.map((d, di) => {
                            const entry = group.entries.find(e => {
                              const ed = new Date(e.date);
                              return ed.getDate() === d.getDate() && ed.getMonth() === d.getMonth();
                            });
                            const code = entry ? TYPE_CODES[entry.type] || '' : '';
                            const colorClass = entry ? TYPE_COLORS[entry.type] || '' : '';
                            const eventCode = entry?.eventType ? EVENT_CODES[entry.eventType] || '' : '';
                            const eventColor = entry?.eventType ? EVENT_COLORS[entry.eventType] || '' : '';
                            // Durée pour tous les événements d'absence (heures planifiées du jour)
                            const ABSENCE_EVT = ['recup_taken', 'cp_partial', 'sick', 'accident', 'maternity', 'unpaid_partial'];
                            let eventHours = null;
                            if (entry?.eventType && ABSENCE_EVT.includes(entry.eventType) && entry?.scheduledHours) {
                              const sched = Number(entry.scheduledHours);
                              const act = entry.actualHours ? Number(entry.actualHours) : 0;
                              const hasWorked = entry.actualStart && entry.actualEnd;
                              if (entry.eventType === 'recup_taken' && entry.overtimeMinutes) {
                                eventHours = Math.abs(entry.overtimeMinutes / 60);
                              } else if (hasWorked && act > 0 && act < sched) {
                                // Partie de journée travaillée : heures manquantes
                                eventHours = sched - act;
                              } else {
                                // Journée complète absente (ou actualHours pas encore recalculé)
                                eventHours = sched;
                              }
                              if (eventHours < 0) eventHours = 0;
                            }
                            return (
                              <td key={di} className="px-1 py-1.5 text-center">
                                {eventCode ? (
                                  <span className={`inline-block px-1 py-0.5 rounded text-[10px] font-semibold ${eventColor}`}>
                                    {eventCode}{eventHours != null && eventHours > 0 ? `:${eventHours.toFixed(2)}h` : ''}
                                  </span>
                                ) : code ? (
                                  <span className={`inline-block px-1 py-0.5 rounded text-[10px] font-semibold ${colorClass}`}>{code}</span>
                                ) : null}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">CP=Congé payé · M=Maladie · AT=Accident · MAT=Maternité · RCR=Repos compensateur · CSS=Sans solde · ABS=Absence · R=Repos</p>
            </div>
          );
        })()}

        {/* ═══ SOUS-ONGLET HEURES (format Silae) ═══ */}
        {detailTab === 'heures' && (() => {
          const copyValue = (val) => {
            navigator.clipboard.writeText(String(val));
            toast.success('Copié');
          };

          // Convertir minutes en décimal : 1h45 = 1.75
          const toDecimal = (hours) => {
            if (!hours || hours <= 0) return 0;
            return parseFloat(hours.toFixed(4));
          };

          const CopyCell = ({ value, label }) => (
            <td className="px-3 py-2 text-center">
              {value ? (
                <div className="flex items-center justify-center gap-1">
                  <span className="font-semibold text-gray-800">{value.toFixed(2)}</span>
                  <button onClick={() => copyValue(value.toFixed(2))} className="p-0.5 text-gray-300 hover:text-indigo-600" title={`Copier ${label}`}>
                    <Copy size={11} />
                  </button>
                </div>
              ) : <span className="text-gray-300">—</span>}
            </td>
          );

          return (
            <div>
              <p className="text-xs text-gray-500 mb-3">Vue Heures — à reporter dans MySilae onglet "Heures". Valeurs en décimal avec majoration.</p>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="min-w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Employé</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">Contrat</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">Base mensuelle</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">HC 10%<br/><span className="text-[9px] text-gray-400">×1.10</span></th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">HC 25%<br/><span className="text-[9px] text-gray-400">×1.25</span></th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">HS 25%<br/><span className="text-[9px] text-gray-400">×1.25</span></th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">HS 50%<br/><span className="text-[9px] text-gray-400">×1.50</span></th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">Taux horaire</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">Coût HS/HC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeGroups.map(group => {
                      const emp = group.employee;
                      const workHours = Number(emp.workHours) || 35;
                      const isPartTime = workHours < 35;
                      const baseMensuelle = parseFloat((workHours * 52 / 12).toFixed(2));

                      // Calculer heures réelles, planifiées par semaine + flags hs_recup/hs_paid
                      const weeklyActual = {};
                      const weeklyScheduled = {};
                      const weeklyHasHsRecup = {}; // semaine a au moins un jour hs_recup
                      const weeklyHasHsPaid = {};  // semaine a au moins un jour hs_paid
                      const TRUE_ABSENCES = ['cp_partial', 'sick', 'accident', 'maternity', 'unpaid_partial', 'ferie'];
                      group.entries.forEach(e => {
                        const wk = getWeekNumber(e.date, dateFrom);
                        if (!weeklyScheduled[wk]) weeklyScheduled[wk] = 0;
                        if (!weeklyActual[wk]) weeklyActual[wk] = 0;
                        const scheduled = e.scheduledHours ? Number(e.scheduledHours) : 0;
                        // Exclure le scheduled des jours d'absence reelle (sinon l'ecart hebdo
                        // serait fausse et le calcul HS deviendrait negatif a tort)
                        if (!TRUE_ABSENCES.includes(e.eventType)) {
                          weeklyScheduled[wk] += scheduled;
                        }
                        if (e.type === 'worked' && !['recup_taken', ...TRUE_ABSENCES].includes(e.eventType)) {
                          const actual = e.actualHours ? Number(e.actualHours) : 0;
                          weeklyActual[wk] += actual;
                        }
                        if (e.eventType === 'hs_recup') weeklyHasHsRecup[wk] = true;
                        if (e.eventType === 'hs_paid') weeklyHasHsPaid[wk] = true;
                      });

                      let hc10Total = 0, hc25Total = 0, hs25Total = 0, hs50Total = 0;

                      // Calcul HS/HC par semaine basé sur total semaine vs planifié
                      Object.keys(weeklyActual).forEach(wk => {
                        const actual = weeklyActual[wk] || 0;
                        const scheduled = weeklyScheduled[wk] || 0;
                        if (actual <= scheduled || scheduled <= 0) return;

                        const weekOTBrut = actual - scheduled;
                        const hasRecup = weeklyHasHsRecup[wk];
                        const hasPaid = weeklyHasHsPaid[wk];

                        // Répartition : si full récup → 0 payé. Si full payé → 0 récup. Si mixte ou ni l'un ni l'autre → tout payé par défaut
                        const hsRecupPart = hasRecup && !hasPaid ? weekOTBrut : 0; // full récup
                        const hsPaidPart = hasRecup && !hasPaid ? 0 : weekOTBrut;  // tout le reste en payé

                        if (isPartTime) {
                          const seuil10 = workHours / 10;
                          const maxHC = workHours / 3;
                          const hcPaid = Math.min(hsPaidPart, maxHC);
                          hc10Total += Math.min(hcPaid, seuil10);
                          hc25Total += Math.max(Math.min(hcPaid, maxHC) - seuil10, 0);
                        } else {
                          hs25Total += Math.min(hsPaidPart, 8);
                          hs50Total += Math.max(hsPaidPart - 8, 0);
                        }
                      });

                      // Avec majoration
                      const hc10Majorees = toDecimal(hc10Total * 1.10);
                      const hc25Majorees = toDecimal(hc25Total * 1.25);
                      const hs25Majorees = toDecimal(hs25Total * 1.25);
                      const hs50Majorees = toDecimal(hs50Total * 1.50);

                      return (
                        <tr key={emp.id} className={`border-b border-gray-50 hover:bg-gray-50/40 ${isPartTime ? 'bg-amber-50/30' : ''}`}>
                          <td className="px-3 py-2 font-medium text-gray-700">
                            {emp.firstName} {emp.lastName}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${isPartTime ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                              {workHours}h
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center text-gray-500">{baseMensuelle}</td>
                          <CopyCell value={hc10Majorees || null} label="HC 10%" />
                          <CopyCell value={hc25Majorees || null} label="HC 25%" />
                          <CopyCell value={hs25Majorees || null} label="HS 25%" />
                          <CopyCell value={hs50Majorees || null} label="HS 50%" />
                          {(() => {
                            const rate = selectedSheet.hourlyRates?.[emp.id];
                            const totalMajorees = (hc10Majorees || 0) + (hc25Majorees || 0) + (hs25Majorees || 0) + (hs50Majorees || 0);
                            const cost = rate && totalMajorees > 0 ? parseFloat((totalMajorees * rate).toFixed(2)) : null;
                            return (
                              <>
                                <td className="px-3 py-2 text-center text-xs text-gray-500">{rate ? `${rate.toFixed(4)} €` : '—'}</td>
                                <CopyCell value={cost} label="Coût" />
                              </>
                            );
                          })()}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                Temps partiel (&lt;35h) : HC 10% = 1/10ème du contrat ×1.10 · HC 25% = au-delà ×1.25 (max 1/3 contrat)<br/>
                Temps plein (≥35h) : HS 25% = les 8 premières HS/semaine ×1.25 · HS 50% = au-delà ×1.50
              </p>
            </div>
          );
        })()}

        {/* ═══ SOUS-ONGLET ÉLÉMENTS VARIABLES (format Silae) ═══ */}
        {detailTab === 'variables' && (() => {
          const copyValue = (val) => {
            navigator.clipboard.writeText(String(val));
            toast.success('Copié');
          };

          const CopyCell = ({ value, label }) => (
            <td className="px-3 py-2 text-center">
              {value ? (
                <div className="flex items-center justify-center gap-1">
                  <span className="font-semibold text-gray-800">{typeof value === 'number' ? value.toFixed(2) : value}</span>
                  <button onClick={() => copyValue(typeof value === 'number' ? value.toFixed(2) : value)} className="p-0.5 text-gray-300 hover:text-indigo-600" title={`Copier ${label}`}>
                    <Copy size={11} />
                  </button>
                </div>
              ) : <span className="text-gray-300">—</span>}
            </td>
          );

          return (
            <div>
              <p className="text-xs text-gray-500 mb-3">Vue Éléments variables — à reporter dans MySilae onglet "Éléments variables"</p>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="min-w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500">Employé</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">T.Transp.</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">NbHRCRA<br/><span className="text-[9px] text-gray-400">Récup (décimal majoré)</span></th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">Indem.Pré</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500">Mutuelle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeGroups.map(group => {
                      const emp = group.employee;
                      const empVars = variables?.find(v => v.employeeId === emp.id);

                      // Calcul NbHRCRA : basé sur total HS semaine (pas par jour)
                      let rcrDecimal = 0;
                      const wkActual = {}, wkScheduled = {}, wkHasRecup = {};
                      const TRUE_ABSENCES_VAR = ['cp_partial', 'sick', 'accident', 'maternity', 'unpaid_partial', 'ferie'];
                      group.entries.forEach(e => {
                        const wk = getWeekNumber(e.date, dateFrom);
                        if (!wkActual[wk]) wkActual[wk] = 0;
                        if (!wkScheduled[wk]) wkScheduled[wk] = 0;
                        // Exclure le scheduled des absences reelles (cumul HS coherent avec l'actual)
                        if (!TRUE_ABSENCES_VAR.includes(e.eventType)) {
                          wkScheduled[wk] += e.scheduledHours ? Number(e.scheduledHours) : 0;
                        }
                        if (e.type === 'worked' && !['recup_taken', ...TRUE_ABSENCES_VAR].includes(e.eventType)) {
                          wkActual[wk] += e.actualHours ? Number(e.actualHours) : 0;
                        }
                        if (e.eventType === 'hs_recup') wkHasRecup[wk] = true;
                      });
                      Object.keys(wkActual).forEach(wk => {
                        if (!wkHasRecup[wk]) return;
                        const ot = (wkActual[wk] || 0) - (wkScheduled[wk] || 0);
                        if (ot > 0) rcrDecimal += ot * 1.25; // total HS semaine × majoration
                      });

                      return (
                        <tr key={emp.id} className="border-b border-gray-50 hover:bg-gray-50/40">
                          <td className="px-3 py-2 font-medium text-gray-700">
                            <button onClick={() => handleGoToEmployee(emp.id)} title="Voir la fiche RH" className={`text-left ${onNavigate ? 'hover:text-primary hover:underline' : ''}`}>
                              {emp.firstName} {emp.lastName}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {emp.transportAmount ? (
                              <div className="flex items-center justify-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={empVars?.transportReceived || false}
                                  onChange={async (e) => {
                                    try {
                                      await updateTimeSheetVariable(selectedSheet.id, emp.id, { transportReceived: e.target.checked });
                                      const updated = await getTimeSheet(selectedSheet.id);
                                      setSelectedSheet(updated.data);
                                    } catch { toast.error('Erreur'); }
                                  }}
                                  disabled={isReadOnly}
                                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-xs text-gray-600">{Number(emp.transportAmount).toFixed(2)} €</span>
                                <button onClick={() => { navigator.clipboard.writeText(Number(emp.transportAmount).toFixed(2)); toast.success('Copié'); }} className="p-0.5 text-gray-300 hover:text-indigo-600"><Copy size={11} /></button>
                              </div>
                            ) : (
                              <span className="text-gray-300 text-xs">Non configuré</span>
                            )}
                          </td>
                          <CopyCell value={rcrDecimal > 0 ? parseFloat(rcrDecimal.toFixed(2)) : null} label="NbHRCRA" />
                          <CopyCell value={null} label="Indem.Pré" />
                          <td className="px-3 py-2 text-center">
                            {emp.mutuelleType === 'entreprise' ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Entreprise</span>
                            ) : emp.mutuelleType === 'conjoint' ? (
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${emp.mutuelleValidUntil && new Date(emp.mutuelleValidUntil) < new Date() ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                Conjoint{emp.mutuelleValidUntil ? ` ${new Date(emp.mutuelleValidUntil).toLocaleDateString('fr-FR')}` : ''}
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">À relancer</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">NbHRCRA = heures sup converties en récup × 1.25 (majoration). T.Transp = montant titre transport (à configurer dans la fiche employé).</p>
            </div>
          );
        })()}
      </div>
    );
  };

  // ─── Rendu liste feuilles ─────────────────────────────────────────────────────

  const renderSheetsTable = (sheetsList) => (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Label</th>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Période</th>
            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Statut</th>
            <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sheetsList.map(sheet => (
            <tr
              key={sheet.id}
              className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors"
            >
              <td className="px-4 py-2.5 font-medium text-gray-800">{sheet.label}</td>
              <td className="px-4 py-2.5 text-gray-500">
                {fmtDateShort(sheet.dateFrom)} → {fmtDateShort(sheet.dateTo)}
              </td>
              <td className="px-4 py-2.5">
                <StatusBadge status={sheet.status} />
              </td>
              <td className="px-4 py-2.5">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => openSheet(sheet)}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 rounded"
                    title="Voir / saisir"
                  >
                    <FileText size={14} />
                  </button>
                  <button
                    onClick={() => handleExportCsv(sheet.id, sheet.label)}
                    className="p-1.5 text-gray-400 hover:text-green-600 rounded"
                    title="Exporter CSV"
                  >
                    <Download size={14} />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(sheet.id)}
                      disabled={deletingId === sheet.id}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded disabled:opacity-50"
                      title="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderSheetsList = () => (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-700">Feuilles de temps</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
        >
          <Plus size={15} /> Nouvelle feuille
        </button>
      </div>

      {loadingSheets ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="default" text="Chargement..." inline />
        </div>
      ) : sheets.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Clock size={40} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucune feuille de temps.</p>
          <p className="text-xs mt-1">Créez votre première feuille pour commencer.</p>
        </div>
      ) : isMulti ? (
        // Groupement par structure
        (() => {
          const grouped = {};
          sheets.forEach(s => {
            const name = s.structureName || 'Inconnue';
            if (!grouped[name]) grouped[name] = [];
            grouped[name].push(s);
          });
          return Object.entries(grouped).map(([structName, structSheets]) => (
            <div key={structName} className="mb-6">
              <h3 className="text-sm font-semibold text-primary mb-2 px-1">{structName}</h3>
              {renderSheetsTable(structSheets)}
            </div>
          ));
        })()
      ) : (
        renderSheetsTable(sheets)
      )}
    </div>
  );

  // ─── Handlers Planning ────────────────────────────────────────────────────────

  const startEditSlot = (empId, week, day, slot) => {
    setEditingSlot({ empId, week, day });
    setSlotForm({ startTime: slot?.startTime || '', endTime: slot?.endTime || '', pauseMinutes: slot?.pauseMinutes ?? 60 });
  };

  const saveSlot = async () => {
    if (!editingSlot) return;
    const { empId, week, day } = editingSlot;
    setSavingSlot(true);
    try {
      if (slotForm.startTime && slotForm.endTime) {
        const result = await upsertSchedule({
          employeeId: empId,
          structureId: currentStructure.id,
          weekNumber: week,
          dayOfWeek: day,
          startTime: slotForm.startTime,
          endTime: slotForm.endTime,
          pauseMinutes: slotForm.pauseMinutes,
          validFrom: new Date().toISOString(),
        });
        // Mise à jour locale du state (évite reload complet)
        const updated = result.data || result;
        setSchedules(prev => {
          const idx = prev.findIndex(s => s.employeeId === empId && s.weekNumber === week && s.dayOfWeek === day);
          if (idx >= 0) { const copy = [...prev]; copy[idx] = { ...prev[idx], ...updated }; return copy; }
          return [...prev, { ...updated, employee: allEmployees.find(e => e.id === empId) || { id: empId, firstName: '?', lastName: '?' } }];
        });
      } else {
        const existing = schedules.find(s => s.employeeId === empId && s.weekNumber === week && s.dayOfWeek === day);
        if (existing) {
          await deleteSchedule(existing.id);
          setSchedules(prev => prev.filter(s => s.id !== existing.id));
        }
      }
      setEditingSlot(null);
    } catch {
      toast.error('Erreur sauvegarde planning');
    }
    setSavingSlot(false);
  };

  const cancelEditSlot = () => {
    setEditingSlot(null);
    setSlotForm({ startTime: '', endTime: '' });
  };

  // ─── Ajouter un employé au planning (créneaux vides) ─────────────────────────
  const handleAddEmployeePlanning = async () => {
    if (!addEmpId) return;
    setSavingSlot(true);
    try {
      const emp = allEmployees.find(e => e.id === addEmpId);
      const workHours = Number(emp?.workHours) || 35;
      // Déduire le nombre de jours travaillés par semaine depuis les heures contractuelles
      const daysPerWeek = workHours <= 28 ? 4 : 5;
      const endTime = workHours <= 28 ? '17:00' : '17:00';
      const validFrom = new Date().toISOString();
      for (let day = 1; day <= daysPerWeek; day++) {
        await upsertSchedule({
          employeeId: addEmpId,
          structureId: currentStructure.id,
          weekNumber: 1, dayOfWeek: day,
          startTime: '09:00', endTime,
          pauseMinutes: 60,
          validFrom,
        });
      }
      setShowAddEmployee(false);
      setAddEmpId('');
      await loadSchedules();
      toast.success('Employé ajouté au planning');
    } catch { toast.error('Erreur ajout'); }
    setSavingSlot(false);
  };

  // ─── Copier horaires d'un employé vers un autre ─────────────────────────────
  const handleCopyEmployee = async () => {
    if (!showCopyEmployee || !copyTargetEmpId) return;
    setSavingSlot(true);
    try {
      const sourceSlots = schedules.filter(s => s.employeeId === showCopyEmployee);
      for (const slot of sourceSlots) {
        await upsertSchedule({
          employeeId: copyTargetEmpId,
          structureId: currentStructure.id,
          weekNumber: slot.weekNumber,
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          pauseMinutes: slot.pauseMinutes ?? 60,
          validFrom: new Date().toISOString(),
        });
      }
      setShowCopyEmployee(null);
      setCopyTargetEmpId('');
      await loadSchedules();
      toast.success('Planning copié');
    } catch { toast.error('Erreur copie'); }
    setSavingSlot(false);
  };

  // ─── Dupliquer une semaine vers les autres pour un employé ────────────────────
  const handleDuplicateWeek = async (empId, sourceWeek) => {
    setSavingSlot(true);
    try {
      const sourceSlots = schedules.filter(s => s.employeeId === empId && s.weekNumber === sourceWeek);
      // Déterminer les semaines existantes pour cet employé
      const empWeeks = [...new Set(schedules.filter(s => s.employeeId === empId).map(s => s.weekNumber))];
      const maxWeek = Math.max(...empWeeks, sourceWeek);
      const targetWeeks = [];
      for (let w = 1; w <= Math.max(maxWeek, 4); w++) {
        if (w !== sourceWeek) targetWeeks.push(w);
      }
      for (const week of targetWeeks) {
        for (const slot of sourceSlots) {
          await upsertSchedule({
            employeeId: empId,
            structureId: currentStructure.id,
            weekNumber: week,
            dayOfWeek: slot.dayOfWeek,
            startTime: slot.startTime,
            endTime: slot.endTime,
            pauseMinutes: slot.pauseMinutes ?? 60,
            validFrom: new Date().toISOString(),
          });
        }
      }
      await loadSchedules();
      toast.success(`Semaine ${sourceWeek} dupliquée`);
    } catch { toast.error('Erreur duplication'); }
    setSavingSlot(false);
  };

  // ─── Supprimer un employé du planning ───────────────────────────────────────
  const handleRemoveEmployeePlanning = async (empId) => {
    if (!window.confirm('Supprimer cet employé du planning ?')) return;
    setSavingSlot(true);
    try {
      const empSlots = schedules.filter(s => s.employeeId === empId);
      for (const slot of empSlots) {
        await deleteSchedule(slot.id);
      }
      await loadSchedules();
      toast.success('Employé retiré du planning');
    } catch { toast.error('Erreur suppression'); }
    setSavingSlot(false);
  };

  // ─── Rendu onglet Planning ────────────────────────────────────────────────────

  const renderPlanning = () => (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-base font-semibold text-gray-700">
            {semaineViewPlan ? semaineViewPlan.nom : 'Vue par semaine'}
          </h2>
          {semaineViewPlan && semaineViewPlan.id === semaineActivePlanId && (
            <span className="text-[10px] bg-green-100 text-green-700 rounded-full px-2 py-0.5 font-medium">Actif aujourd'hui</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {semaineAllPlans.length > 1 && (
            <select
              value={semaineSelectedPlanId || ''}
              onChange={e => setSemaineSelectedPlanId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white"
            >
              {semaineAllPlans.map(p => (
                <option key={p.id} value={p.id}>
                  {p.nom}{p.id === semaineActivePlanId ? ' ✓ actif' : ''}
                </option>
              ))}
            </select>
          )}
          <select value={planningFilterEmpId} onChange={e => setPlanningFilterEmpId(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs bg-white">
            <option value="">Tous les employés</option>
            {employeeIds.map(id => {
              const emp = planningGrid[id]?.employee;
              return <option key={id} value={id}>{emp?.firstName} {emp?.lastName}</option>;
            })}
          </select>
          <button onClick={async () => {
            const { generatePlanningPdf } = await import('../utils/planningPdf');
            const { getStructureIdentity } = await import('../api');
            let structName = currentStructure?.name || '';
            try {
              const res = await getStructureIdentity(currentStructure?.id);
              if (res.data?.fullName) structName = res.data.fullName;
            } catch {}
            const result = await generatePlanningPdf(planningGrid, planningWeeks, structName, currentStructure?.id, { preview: true });
            if (result) setPdfPreview(result);
          }} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50">
            <FileText size={13} /> Export PDF
          </button>
        </div>
      </div>


      {loadingSchedules ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="default" text="Chargement..." inline />
        </div>
      ) : employeeIds.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users size={40} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucun planning configuré pour cette structure.</p>
          <p className="text-xs mt-1">Importez un planning depuis l'onglet admin ou ajoutez les employés.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 min-w-[60px]">Sem.</th>
                <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 min-w-[140px]">Employé</th>
                {DAYS_FR.map(d => (
                  <th key={d} className="px-3 py-2.5 text-center text-xs font-medium text-gray-500">{d}</th>
                ))}
                <th className="px-2 py-2.5 text-center text-xs font-medium text-gray-500 w-14">Total</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: planningWeeks }, (_, i) => i + 1).map(week => (
                <React.Fragment key={week}>
                  {employeeIds.filter(id => !planningFilterEmpId || id === planningFilterEmpId).map((empId, empIdx) => {
                    const empData = planningGrid[empId];
                    const empName = empData.employee
                      ? `${empData.employee.firstName} ${empData.employee.lastName}`
                      : `Employé ${empId}`;
                    const colorClass = getEmployeeColorByIndex(empIdx);
                    const weekData = empData.weeks[week] || {};

                    // Calcul total semaine (avec pause par créneau)
                    let weekTotal = 0;
                    [1,2,3,4,5].forEach(day => {
                      const slot = weekData[day];
                      if (slot?.startTime && slot?.endTime) {
                        const [sh,sm] = slot.startTime.split(':').map(Number);
                        const [eh,em] = slot.endTime.split(':').map(Number);
                        const pause = slot.pauseMinutes ?? 60;
                        const mins = (eh*60+em) - (sh*60+sm) - pause;
                        if (mins > 0) weekTotal += mins / 60;
                      }
                    });

                    return (
                      <tr
                        key={`${week}-${empId}`}
                        className="border-b border-gray-50 hover:bg-gray-50/40"
                      >
                        {empIdx === 0 ? (
                          <td
                            className="px-3 py-1.5 font-semibold text-gray-500 border-r border-gray-100"
                            rowSpan={planningFilterEmpId ? 1 : employeeIds.length}
                          >
                            S{week}
                          </td>
                        ) : null}
                        <td className="px-3 py-1.5">
                          <button
                            onClick={() => handleGoToEmployee(empId)}
                            title="Voir la fiche RH"
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClass} ${onNavigate ? 'hover:opacity-75 cursor-pointer' : 'cursor-default'}`}
                          >
                            {empName}
                          </button>
                        </td>
                        {[1, 2, 3, 4, 5].map(day => {
                          const slot = weekData[day];
                          return (
                            <td key={day} className="px-1 py-1 text-center">
                              <div className={`px-1.5 py-1 rounded text-xs font-medium ${slot ? colorClass : 'text-gray-200'}`}>
                                {slot?.startTime && slot?.endTime ? `${slot.startTime}–${slot.endTime}` : '—'}
                              </div>
                            </td>
                          );
                        })}
                        <td className={`px-2 py-1.5 text-center text-xs font-semibold ${weekTotal === Number(empData.employee?.workHours || 35) ? 'text-green-600' : weekTotal > Number(empData.employee?.workHours || 35) ? 'text-red-600' : 'text-amber-600'}`}>
                          {weekTotal > 0 ? `${weekTotal}h` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Séparateur entre semaines */}
                  <tr>
                    <td colSpan={8} className="h-1 bg-gray-100/60" />
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ─── Modal création ───────────────────────────────────────────────────────────

  const renderCreateModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Nouvelle feuille de temps</h3>
          <button
            onClick={() => setShowCreateModal(false)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Structure (multi) */}
          {isMulti && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Structure <span className="text-red-400">*</span>
              </label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                value={createForm.structureId}
                onChange={e => setCreateForm(f => ({ ...f, structureId: e.target.value }))}
              >
                <option value="">— Sélectionner —</option>
                {structures.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="ex : Janvier 2026"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={createForm.label}
              onChange={e => setCreateForm(f => ({ ...f, label: e.target.value }))}
            />
          </div>

          {/* Date début */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date début <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                value={createForm.dateFrom}
                onChange={e => setCreateForm(f => ({ ...f, dateFrom: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date fin <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                value={createForm.dateTo}
                onChange={e => setCreateForm(f => ({ ...f, dateTo: e.target.value }))}
              />
            </div>
          </div>

          {/* Aperçu plan appliqué */}
          {createForm.dateFrom && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <Info size={13} className="text-blue-500 mt-0.5 shrink-0" />
              <div className="text-xs text-blue-700">
                {previewPlanLoading ? (
                  <span className="text-gray-400">Chargement…</span>
                ) : previewPlan ? (
                  <>
                    <span className="font-medium">Plan appliqué :</span>{' '}
                    {previewPlan.nom}
                    {!previewPlan.isReference && previewPlan.dateDebut && (
                      <span className="text-blue-500">
                        {' '}({fmtDate(previewPlan.dateDebut)} → {fmtDate(previewPlan.dateFin)})
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-amber-600">Aucun plan de planning configuré pour cette structure.</span>
                )}
              </div>
            </div>
          )}

          {/* Semaine de départ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              Semaine de départ du cycle
              <span
                title="Quelle semaine du planning correspond au premier jour de la période ?"
                className="text-gray-400 cursor-help"
              >
                <Info size={13} />
              </span>
            </label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={createForm.startWeek}
              onChange={e => setCreateForm(f => ({ ...f, startWeek: e.target.value }))}
            >
              <option value={1}>Semaine 1</option>
              <option value={2}>Semaine 2</option>
              <option value={3}>Semaine 3</option>
              <option value={4}>Semaine 4</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            onClick={() => setShowCreateModal(false)}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {creating ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
            Créer et préremplir
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Render principal ─────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modal création */}
      {showCreateModal && renderCreateModal()}

      {/* Loader détail feuille */}
      {loadingDetail && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-xl px-6 py-4 shadow-lg flex items-center gap-2 text-gray-600">
            <Loader size={18} className="animate-spin" /> Chargement de la feuille...
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Titre */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-100 rounded-xl">
            <Clock size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Planning</h1>
            <p className="text-sm text-gray-500">{isMulti ? 'Toutes les structures' : (currentStructure?.name || 'Structure')}</p>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => { setActiveTab('plans'); setSelectedSheet(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
              ${activeTab === 'plans'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Calendar size={14} className="inline mr-1.5 -mt-0.5" />
            Modèles de planning
          </button>
          <button
            onClick={() => { setActiveTab('semaine'); setSelectedSheet(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
              ${activeTab === 'semaine'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Users size={14} className="inline mr-1.5 -mt-0.5" />
            Vue par semaine
          </button>
          <button
            onClick={() => { setActiveTab('sheets'); setSelectedSheet(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px
              ${activeTab === 'sheets'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Clock size={14} className="inline mr-1.5 -mt-0.5" />
            Feuilles de temps
          </button>
        </div>

        {/* Corps onglet */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          {activeTab === 'sheets' && (
            selectedSheet
              ? renderSheetDetail()
              : renderSheetsList()
          )}
          {activeTab === 'plans' && (
            <GlobalPlanningTab
              structureId={currentStructure?.id}
              structureName={currentStructure?.name || ''}
              employees={allEmployees.filter(e =>
                e.structureId === currentStructure?.id
                || e.secondaryStructures?.some(s => s.structureId === currentStructure?.id)
              )}
            />
          )}
          {activeTab === 'semaine' && renderPlanning()}
        </div>
      </div>

      {pdfPreview && (
        <PdfPreviewModal
          doc={pdfPreview.doc}
          fileName={pdfPreview.fileName}
          onClose={() => setPdfPreview(null)}
        />
      )}

      {/* Modale de sélection employés — Feuilles papier PDF */}
      {showBlankPdfModal && selectedSheet && (() => {
        // Employés uniques dans la feuille, triés par nom
        const empMap = new Map();
        (selectedSheet.entries || []).forEach(e => {
          if (e.employee && !empMap.has(e.employeeId)) empMap.set(e.employeeId, e.employee);
        });
        const employees = Array.from(empMap.values()).sort((a, b) =>
          (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')
        );
        const allIds = employees.map(e => e.id);
        const sid = selectedSheet.structureId || currentStructure?.id || '';
        const hiddenKey = `timesheet-blank-hidden-emp-${sid}`;

        const toggle = (id) => {
          setBlankPdfSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
          );
        };
        const selectAll = () => setBlankPdfSelectedIds(allIds);
        const selectNone = () => setBlankPdfSelectedIds([]);

        const handleGenerate = async () => {
          if (blankPdfSelectedIds.length === 0) {
            toast.error('Sélectionne au moins un employé');
            return;
          }
          setBlankPdfGenerating(true);
          try {
            // Mémoriser les décochés en localStorage (par structure)
            const hidden = allIds.filter(id => !blankPdfSelectedIds.includes(id));
            try {
              localStorage.setItem(hiddenKey, JSON.stringify(hidden));
            } catch { /* quota */ }

            const { generateBlankTimesheetPdf } = await import('../utils/timesheetBlankPdf');
            const { getStructureIdentity } = await import('../api');
            let structName = currentStructure?.name || '';
            try {
              const res = await getStructureIdentity(sid);
              if (res.data?.fullName) structName = res.data.fullName;
            } catch {}
            const result = await generateBlankTimesheetPdf(selectedSheet, structName, {
              preview: true,
              selectedEmployeeIds: blankPdfSelectedIds,
            });
            if (result) setPdfPreview(result);
            setShowBlankPdfModal(false);
          } catch (e) {
            console.error('[BlankPdf] generate:', e);
            toast.error('Erreur génération PDF');
          } finally {
            setBlankPdfGenerating(false);
          }
        };

        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !blankPdfGenerating && setShowBlankPdfModal(false)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Feuilles papier — sélection des employés</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Ceux que tu décoches seront mémorisés pour cette structure.
                  </p>
                </div>
                <button
                  onClick={() => setShowBlankPdfModal(false)}
                  disabled={blankPdfGenerating}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-40"
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="px-5 py-2 border-b border-gray-100 flex items-center gap-2 text-xs">
                <button onClick={selectAll} className="text-indigo-600 hover:text-indigo-800 font-medium">Tout sélectionner</button>
                <span className="text-gray-300">·</span>
                <button onClick={selectNone} className="text-gray-500 hover:text-gray-700 font-medium">Tout désélectionner</button>
                <span className="ml-auto text-gray-400">{blankPdfSelectedIds.length} / {employees.length}</span>
              </div>

              <div className="flex-1 overflow-y-auto px-2 py-2">
                {employees.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-sm">Aucun employé sur cette feuille.</div>
                ) : (
                  employees.map(emp => {
                    const checked = blankPdfSelectedIds.includes(emp.id);
                    return (
                      <label
                        key={emp.id}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition ${checked ? 'bg-indigo-50/40 hover:bg-indigo-50' : 'hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(emp.id)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className={`text-sm ${checked ? 'text-gray-800' : 'text-gray-400'}`}>
                          <span className="font-medium">{(emp.lastName || '').toUpperCase()}</span> {emp.firstName || ''}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowBlankPdfModal(false)}
                  disabled={blankPdfGenerating}
                  className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                >
                  Annuler
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={blankPdfGenerating || blankPdfSelectedIds.length === 0}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {blankPdfGenerating ? <Loader size={14} className="animate-spin" /> : <FileText size={14} />}
                  Générer PDF ({blankPdfSelectedIds.length} employé{blankPdfSelectedIds.length > 1 ? 's' : ''})
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TimesheetPage;
